import { Fragment, useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthLite } from '@/context/AuthLite';
import ProfileList from '@/components/mappings/ProfileList';
import ProfileModal from '@/components/mappings/ProfileModal';
import AccessDeniedMessage from '@/components/AccessDeniedMessage';
import { AuditChangeSummary } from '@/components/settings/AuditChangeSummary';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/components/ThemeProvider';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Loader2, Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageTitleBar } from '@/components/ui/PageTitleBar';
import { apiUrl } from '@/lib/api-client';
import {
  AUDIT_CATEGORY_CHIPS,
  getAuditActionLabel,
  getAuditSummaryLocalized,
  getAuditDeepLink,
  getAuditTechnicalSecondLine,
  getRowCategory,
  getAuditPayloadChanged,
  shouldShowAuditChangeList,
  type AuditTableRow,
  type AuditCategory,
} from '@/lib/audit-log-display';
import { stageMatchFingerprint } from '@shared/ai-lead-stage-fingerprint';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { SettingsNavPanel } from '@/components/settings/SettingsNavPanel';
import { useSettingsUrlSync } from '@/components/settings/use-settings-url-sync';
import {
  ALL_SETTINGS_TAB_IDS,
  buildSettingsMenuGroups,
  EMAIL_DOCS_SUB_ITEMS,
  SETTINGS_TAB_SR_KEYS,
  VARIABLES_SUB_ITEMS,
  VALID_EMAIL_DOCS_SUBTABS,
  VALID_VARIABLES_SUBTABS,
  type EmailDocsSubTab,
  type SettingsTab,
  type VariablesSubTab,
} from '@/components/settings/settings-nav-shared';
import { toast } from '@/hooks/use-toast';

const AUDIT_PAGE_SIZE = 100;

const AdminUsers = lazy(() => import('@/components/AdminUsers'));
const FieldMappingsManager = lazy(() => import('@/components/mappings/FieldMappingsManager'));
const ZapierStyleMapper = lazy(() => import('@/components/mappings/ZapierStyleMapper'));
const TestPanel = lazy(() => import('@/components/mappings/TestPanel'));
const ManualsHierarchyManager = lazy(() => import('@/components/manuals/ManualsHierarchyManager'));
const EmailTemplatesManager = lazy(() => import('@/components/email/EmailTemplatesManager'));
const EmailAccountsManager = lazy(() => import('@/components/email/EmailAccountsManager'));
const ContractTemplatesManager = lazy(async () => {
  const mod = await import('@/components/crm/ContractTemplatesManager');
  return { default: mod.ContractTemplatesManager };
});
const PriceGeneratorPanel = lazy(() => import('@/components/price-generator/PriceGeneratorPanel'));
const AccessConfiguratorPanel = lazy(() => import('@/components/access-configurator/AccessConfiguratorPanel'));
const SubPipelinesManager = lazy(() => import('@/components/crm/SubPipelinesManager'));
const RouteGeneratorGlobalSettingsPanel = lazy(() => import('@/components/settings/RouteGeneratorGlobalSettingsPanel'));
const ProductTypesManager = lazy(() => import('@/components/crm/ProductTypesManager'));
const ImageLookupsManager = lazy(() => import('@/components/images/ImageLookupsManager'));
const GoogleContactsSyncPanel = lazy(() => import('@/components/settings/GoogleContactsSyncPanel'));

export default function Settings() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'sk' ? 'sk-SK' : i18n.language === 'hu' ? 'hu-HU' : 'en-GB';
  const { user, authMode, refreshUser } = useAuthLite();
  const navigate = useNavigate();
  const location = useLocation();
  const { section, subsection } = useParams<{ section?: string; subsection?: string }>();
  const isMobile = useIsMobile();
  const [clientId, setClientId] = useState<string>(
    import.meta.env.VITE_GOOGLE_CLIENT_ID || 
    localStorage.getItem('google_client_id') || 
    ''
  );
  const [folderId, setFolderId] = useState<string>('');
  const [saved, setSaved] = useState(false);
  const [stages, setStages] = useState<any[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  /** subpipeline id → cél stage id (bemérés után) */
  const [measureBySubpipeline, setMeasureBySubpipeline] = useState<Record<string, string>>({});
  const [supplierStampImage, setSupplierStampImage] = useState<File | null>(null);
  const [supplierStampImageUrl, setSupplierStampImageUrl] = useState<string>('');
  const [automationOnTaxDocumentZmluvaEmail, setAutomationOnTaxDocumentZmluvaEmail] = useState<boolean>(false);
  const [closingDateOnStageEnabled, setClosingDateOnStageEnabled] = useState<boolean>(false);
  const [closingDateOffsetByStageId, setClosingDateOffsetByStageId] = useState<Record<string, number>>({});
  const [closingDateOnStagePipelineId, setClosingDateOnStagePipelineId] = useState<string>("__all__");
  /** subpipeline id → sablon stage id (számla befizetése után) */
  const [invoicePaidBySubpipeline, setInvoicePaidBySubpipeline] = useState<Record<string, string>>({});
  const legacyInvoiceStageOnlyRef = useRef<string | null>(null);
  const legacyMeasureStageOnlyRef = useRef<string | null>(null);

  type AiLeadCatRow = {
    key: string;
    label: string;
    description: string;
    stageCanonical: string;
    legacyStageId?: string;
    sendQuoteEmail?: boolean;
  };
  const defaultAiLeadCategories = (): AiLeadCatRow[] => [
    { key: '', label: '', description: '', stageCanonical: '', sendQuoteEmail: true },
    { key: '', label: '', description: '', stageCanonical: '', sendQuoteEmail: true },
    { key: '', label: '', description: '', stageCanonical: '', sendQuoteEmail: true },
  ];
  const [aiLeadClassEnabled, setAiLeadClassEnabled] = useState(false);
  const [aiLeadCategories, setAiLeadCategories] = useState<AiLeadCatRow[]>(defaultAiLeadCategories);
  /** Szakasz ujjlenyomat (minden subpipeline-ban közös szakaszok egyike). */
  const [aiEmptyNoteCanonical, setAiEmptyNoteCanonical] = useState('');
  const [aiEmptyNoteLegacyStageId, setAiEmptyNoteLegacyStageId] = useState('');
  const [aiEmptyNoteSendQuoteEmail, setAiEmptyNoteSendQuoteEmail] = useState(true);

  // Mappings state
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<any | null>(null);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('profile');
  const [variablesSubTab, setVariablesSubTab] = useState<VariablesSubTab>('product-types');
  const [emailDocsSubTab, setEmailDocsSubTab] = useState<EmailDocsSubTab>('email-templates');
  const [settingsNavFilter, setSettingsNavFilter] = useState('');
  const [mobileSettingsNavOpen, setMobileSettingsNavOpen] = useState(false);

  // Személyes beállítások (preferenciák)
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [prefCrmSortBy, setPrefCrmSortBy] = useState<string>('createdAt');
  const [prefCrmSortOrder, setPrefCrmSortOrder] = useState<'asc' | 'desc'>('desc');
  const [prefCrmStartExpanded, setPrefCrmStartExpanded] = useState(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [preferencesSaved, setPreferencesSaved] = useState(false);

  // Profil: megjelenített név, profilkép, jelszóváltás
  const [profileName, setProfileName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const profileFileInputRef = useRef<HTMLInputElement>(null);
  const stampFileInputRef = useRef<HTMLInputElement>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Audit napló (Admin)
  const [auditEntries, setAuditEntries] = useState<AuditTableRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditExportLoading, setAuditExportLoading] = useState(false);
  const [auditFilterUser, setAuditFilterUser] = useState('');
  const [auditFilterAction, setAuditFilterAction] = useState('');
  const [auditFilterEntityType, setAuditFilterEntityType] = useState('');
  const [auditFilterFrom, setAuditFilterFrom] = useState('');
  const [auditFilterTo, setAuditFilterTo] = useState('');
  const [auditCategoryFilter, setAuditCategoryFilter] = useState<AuditCategory | 'all'>('all');
  const [auditExpandedId, setAuditExpandedId] = useState<string | null>(null);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditLoadingMore, setAuditLoadingMore] = useState(false);
  const [auditOnlyWithDiff, setAuditOnlyWithDiff] = useState(false);
  const [auditDetailEntry, setAuditDetailEntry] = useState<AuditTableRow | null>(null);
  const auditEntriesRef = useRef<AuditTableRow[]>([]);
  auditEntriesRef.current = auditEntries;

  // Error logs (Admin)
  const [errorLogs, setErrorLogs] = useState<Array<{ id?: string; timestamp?: string; operation?: string; message?: string; error_message?: string; error_stack?: string; user_email?: string; data?: unknown }>>([]);
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [errorLogsFilterOp, setErrorLogsFilterOp] = useState('');

  const { theme, setTheme, designVariant, setDesignVariant } = useTheme();

  const settingsMenuGroups = useMemo(() => buildSettingsMenuGroups(user?.role === 'Admin'), [user?.role]);

  const [companySettingsHydrated, setCompanySettingsHydrated] = useState(false);
  const companyBaselineRef = useRef<string | null>(null);
  const [companyDirty, setCompanyDirty] = useState(false);

  const getCompanyFingerprint = useCallback(() => {
    return JSON.stringify({
      folderId,
      clientId,
      supplierStampImageUrl,
      automationOnTaxDocumentZmluvaEmail,
      invoicePaidBySubpipeline,
      measureBySubpipeline,
      aiLeadClassEnabled,
      aiLeadCategories,
      aiEmptyNoteCanonical,
      aiEmptyNoteLegacyStageId,
      aiEmptyNoteSendQuoteEmail,
      pendingStamp: !!supplierStampImage,
    });
  }, [
    folderId,
    clientId,
    supplierStampImageUrl,
    automationOnTaxDocumentZmluvaEmail,
    invoicePaidBySubpipeline,
    measureBySubpipeline,
    aiLeadClassEnabled,
    aiLeadCategories,
    aiEmptyNoteCanonical,
    aiEmptyNoteLegacyStageId,
    aiEmptyNoteSendQuoteEmail,
    supplierStampImage,
  ]);

  useEffect(() => {
    if (user?.role !== 'Admin' || !companySettingsHydrated) return;
    if (companyBaselineRef.current === null) {
      companyBaselineRef.current = getCompanyFingerprint();
      setCompanyDirty(false);
      return;
    }
    setCompanyDirty(getCompanyFingerprint() !== companyBaselineRef.current);
  }, [user?.role, companySettingsHydrated, getCompanyFingerprint]);

  useEffect(() => {
    if (!companyDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [companyDirty]);

  const trySelectSettingsTab = useCallback(
    (next: SettingsTab) => {
      if (companyDirty && settingsTab === 'company' && next !== 'company') {
        if (!window.confirm(t('settings.companyUnsavedLeave'))) return;
      }
      setSettingsTab(next);
    },
    [companyDirty, settingsTab, t]
  );

  const navFilterLower = settingsNavFilter.trim().toLowerCase();
  const labelMatchesFilter = useCallback(
    (labelKey: string) => !navFilterLower || t(labelKey).toLowerCase().includes(navFilterLower),
    [navFilterLower, t]
  );
  const lazyPanelFallback = (
    <div className="py-8 text-sm text-muted-foreground flex items-center gap-2">
      <Loader2 className="h-4 w-4 animate-spin" />
      {t('common.loading')}
    </div>
  );

  useSettingsUrlSync({
    section,
    subsection,
    pathname: location.pathname,
    navigate,
    settingsTab,
    variablesSubTab,
    emailDocsSubTab,
    setSettingsTab,
    setVariablesSubTab,
    setEmailDocsSubTab,
  });

  useEffect(() => {
    (async () => {
      const res = await fetch('/api/settings');
      if (res.ok) {
        const data = await res.json();
        setFolderId(data.manualsFolderId || '');
        setSupplierStampImageUrl(data.supplierStampImageUrl || '');
        setAutomationOnTaxDocumentZmluvaEmail(!!data.automations?.onTaxDocumentZmluvaEmail?.enabled);
        setClosingDateOnStageEnabled(!!data.automations?.closingDateOnStage?.enabled);
        const cdMapRaw = data.automations?.closingDateOnStage?.offsetDaysByStageId;
        const cdMap: Record<string, number> = {};
        if (cdMapRaw && typeof cdMapRaw === 'object') {
          for (const [k, v] of Object.entries(cdMapRaw as Record<string, unknown>)) {
            const n = Number(v);
            if (k && Number.isFinite(n)) cdMap[k] = Math.max(0, Math.min(7, Math.trunc(n)));
          }
        }
        setClosingDateOffsetByStageId(cdMap);

        const invPaid = data.automations?.onInvoicePaid;
        const invMap: Record<string, string> = {};
        if (invPaid?.stagesBySubpipelineId && typeof invPaid.stagesBySubpipelineId === 'object') {
          Object.assign(invMap, invPaid.stagesBySubpipelineId);
        }
        if (invPaid?.pipelineId && invPaid?.stageId) {
          invMap[String(invPaid.pipelineId)] = String(invPaid.stageId);
        }
        setInvoicePaidBySubpipeline(invMap);
        legacyInvoiceStageOnlyRef.current = null;
        if (Object.keys(invMap).length === 0 && invPaid?.stageId?.trim()) {
          legacyInvoiceStageOnlyRef.current = String(invPaid.stageId).trim();
        }

        const onMc = data.automations?.onMeasureComplete;
        const mMap: Record<string, string> = {};
        if (onMc?.stagesBySubpipelineId && typeof onMc.stagesBySubpipelineId === 'object') {
          Object.assign(mMap, onMc.stagesBySubpipelineId);
        }
        setMeasureBySubpipeline(mMap);
        legacyMeasureStageOnlyRef.current = null;
        if (Object.keys(mMap).length === 0 && data.measureTargetStageId?.trim()) {
          legacyMeasureStageOnlyRef.current = String(data.measureTargetStageId).trim();
        }

        const ai = data.automations?.aiLeadClassification;
        if (ai && Array.isArray(ai.categories) && ai.categories.length > 0) {
          setAiLeadClassEnabled(!!ai.enabled);
          setAiLeadCategories(
            ai.categories.map(
              (c: {
                key?: string;
                label?: string;
                description?: string;
                stageCanonical?: string;
                stageId?: string;
                sendQuoteEmail?: boolean;
              }) => {
                const sc = (c.stageCanonical ?? '').trim();
                const leg = (c.stageId ?? '').trim();
                return {
                  key: c.key ?? '',
                  label: c.label ?? '',
                  description: c.description ?? '',
                  stageCanonical: sc,
                  legacyStageId: !sc && leg ? leg : undefined,
                  sendQuoteEmail: c.sendQuoteEmail !== false,
                };
              }
            )
          );
          const en = ai.emptyNote ?? {};
          const eCanon = (en.stageCanonical ?? '').trim();
          const eLeg = (en.stageId ?? '').trim();
          setAiEmptyNoteCanonical(eCanon);
          setAiEmptyNoteLegacyStageId(!eCanon && eLeg ? eLeg : '');
          setAiEmptyNoteSendQuoteEmail(en.sendQuoteEmail !== false);
        } else {
          setAiLeadClassEnabled(false);
          setAiLeadCategories(defaultAiLeadCategories());
          setAiEmptyNoteCanonical('');
          setAiEmptyNoteLegacyStageId('');
        }
        setCompanySettingsHydrated(true);
      }
    })();
  }, []);


  useEffect(()=>{
    setLoadingStages(true);
    fetch('/api/crm/stages', { credentials: 'include' })
      .then(async (r)=> {
        if (r.status === 403) return { unauthorized: true } as any;
        return r.json();
      })
      .then((j:any)=> { if (!j?.unauthorized) setStages(j.stages || []); })
      .finally(()=> setLoadingStages(false));
  }, []);

  useEffect(() => {
    setLoadingPipelines(true);
    fetch('/api/crm/pipelines', { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 403) return { unauthorized: true } as any;
        return r.json();
      })
      .then((j: any) => {
        if (!j?.unauthorized) setPipelines(j.pipelines || []);
      })
      .finally(() => setLoadingPipelines(false));
  }, []);

  const subpipelines = useMemo(
    () => pipelines.filter((p: any) => (p.parentPipelineId ?? p.parent_pipeline_id) != null),
    [pipelines]
  );

  const pipelineLabel = (p: any) =>
    p?.name_hu || p?.name_sk || p?.name_en || p?.name || p?.id || '';

  const stageLabel = (s: any) =>
    s?.name_hu || s?.name_sk || s?.name_en || s?.name || s?.id || '';

  const stagePipelineId = (s: any) => String(s?.pipelineId ?? s?.pipeline_id ?? '');

  const stageOptionLabel = (s: any) => {
    const pid = stagePipelineId(s);
    const pl = pipelines.find((p: any) => p.id === pid);
    const pnm = pl ? pipelineLabel(pl) : pid;
    return `${pnm} — ${stageLabel(s)}`;
  };

  const closingDatePipelineOptions = useMemo(() => {
    return [
      { id: "__all__", label: t("common.all") },
      ...subpipelines.map((p: any) => ({ id: p.id, label: pipelineLabel(p) })),
    ];
  }, [subpipelines, t]);

  const closingDateStages = useMemo(() => {
    if (closingDateOnStagePipelineId === "__all__") return stages;
    return stages.filter((s: any) => stagePipelineId(s) === closingDateOnStagePipelineId);
  }, [stages, closingDateOnStagePipelineId]);

  /** Csak olyan szakaszok, amelyek mindegyik alpipeline-ban előfordulnak (azonos név/csoport alapján). */
  const aiLeadCommonStageOptions = useMemo(() => {
    if (!subpipelines.length || !stages.length) return [] as { fingerprint: string; label: string }[];
    const subIds = subpipelines.map((p: any) => p.id);
    const bySub = subIds.map((sid) => {
      const set = new Set<string>();
      for (const st of stages) {
        if (stagePipelineId(st) === sid) set.add(stageMatchFingerprint(st));
      }
      return set;
    });
    let inter = bySub[0]! ?? new Set<string>();
    for (let i = 1; i < bySub.length; i++) {
      inter = new Set([...inter].filter((x) => bySub[i]!.has(x)));
    }
    const firstSid = subIds[0]!;
    return [...inter].map((fp) => {
      const st = stages.find(
        (s: any) => stagePipelineId(s) === firstSid && stageMatchFingerprint(s) === fp
      );
      return {
        fingerprint: fp,
        label: st ? stageLabel(st) : fp.slice(0, 40),
      };
    });
  }, [stages, subpipelines]);

  useEffect(() => {
    if (!stages.length || !subpipelines.length) return;
    setAiLeadCategories((prev) =>
      prev.map((row) => {
        if (row.stageCanonical.trim()) return { ...row, legacyStageId: undefined };
        const leg = row.legacyStageId?.trim();
        if (!leg) return row;
        const st = stages.find((s: any) => s.id === leg);
        if (!st) return row;
        return {
          ...row,
          stageCanonical: stageMatchFingerprint(st),
          legacyStageId: undefined,
        };
      })
    );
  }, [stages, subpipelines]);

  useEffect(() => {
    if (!stages.length) return;
    const leg = aiEmptyNoteLegacyStageId?.trim();
    if (!leg || aiEmptyNoteCanonical.trim()) return;
    const st = stages.find((s: any) => s.id === leg);
    if (!st) return;
    setAiEmptyNoteCanonical(stageMatchFingerprint(st));
    setAiEmptyNoteLegacyStageId('');
  }, [stages, aiEmptyNoteLegacyStageId, aiEmptyNoteCanonical]);

  useEffect(() => {
    if (!subpipelines.length) return;
    if (legacyInvoiceStageOnlyRef.current) {
      const sid = legacyInvoiceStageOnlyRef.current;
      legacyInvoiceStageOnlyRef.current = null;
      setInvoicePaidBySubpipeline((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        const n: Record<string, string> = {};
        for (const p of subpipelines) n[p.id] = sid;
        return n;
      });
    }
    if (legacyMeasureStageOnlyRef.current) {
      const sid = legacyMeasureStageOnlyRef.current;
      legacyMeasureStageOnlyRef.current = null;
      setMeasureBySubpipeline((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        const n: Record<string, string> = {};
        for (const p of subpipelines) n[p.id] = sid;
        return n;
      });
    }
  }, [subpipelines]);

  useEffect(() => {
    setSaved(false);
  }, [
    clientId,
    folderId,
    measureBySubpipeline,
    invoicePaidBySubpipeline,
    aiLeadClassEnabled,
    aiLeadCategories,
    aiEmptyNoteCanonical,
    aiEmptyNoteLegacyStageId,
    automationOnTaxDocumentZmluvaEmail,
    closingDateOnStageEnabled,
    closingDateOffsetByStageId,
    closingDateOnStagePipelineId,
  ]);

  // Load mappings profiles
  useEffect(() => {
    loadProfiles();
  }, []);

  // Load user preferences (for Személyes beállítások tab)
  useEffect(() => {
    if (!user?.email) return;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/user/preferences'), { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const p = data.preferences || {};
          if (p.crmSortBy) setPrefCrmSortBy(p.crmSortBy);
          if (p.crmSortOrder) setPrefCrmSortOrder(p.crmSortOrder);
          setPrefCrmStartExpanded(!p.crmCollapsedColumns || Object.keys(p.crmCollapsedColumns).length === 0);
        }
      } catch (e) {
        console.error('Failed to load preferences:', e);
      } finally {
        setPreferencesLoaded(true);
      }
    })();
  }, [user?.email]);

  useEffect(() => {
    if (user?.name !== undefined) setProfileName(user.name ?? '');
  }, [user?.name]);

  const saveProfile = async () => {
    if (!user?.email) return;
    setProfileSaving(true);
    setProfileSaved(false);
    try {
      let res: Response;
      if (profilePictureFile) {
        const formData = new FormData();
        formData.append('name', profileName.trim() || user?.email || '');
        formData.append('picture', profilePictureFile);
        res = await fetch(apiUrl('/api/user/profile'), {
          method: 'PATCH',
          credentials: 'include',
          body: formData,
        });
        if (res.ok) {
          setProfilePictureFile(null);
          setProfilePicturePreview(null);
        }
      } else {
        res = await fetch(apiUrl('/api/user/profile'), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: profileName.trim() || user?.email }),
        });
      }
      if (res.ok) {
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 3000);
        await refreshUser();
      }
    } catch (e) {
      console.error('Failed to save profile:', e);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const res = await fetch(apiUrl('/api/user/export'), { credentials: 'include' });
      if (!res.ok) return;
      const blob = await res.blob();
      const disp = res.headers.get('Content-Disposition');
      const match = disp?.match(/filename="?([^";]+)"?/);
      const name = match?.[1] || `adat-export-${Date.now()}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExportLoading(false);
    }
  };

  const [logoutOthersLoading, setLogoutOthersLoading] = useState(false);
  const [logoutOthersDone, setLogoutOthersDone] = useState(false);
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');

  const handleLogoutOthers = async () => {
    if (!confirm(t('confirm.logoutAll'))) return;
    setLogoutOthersLoading(true);
    setLogoutOthersDone(false);
    try {
      const res = await fetch(apiUrl('/api/auth/logout-others'), { method: 'POST', credentials: 'include' });
      if (res.ok) {
        setLogoutOthersDone(true);
        setTimeout(() => setLogoutOthersDone(false), 5000);
      }
    } catch (e) {
      console.error('Logout others failed:', e);
    } finally {
      setLogoutOthersLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess(false);
    if (newPassword !== confirmPassword) {
      setPasswordError(t('settings.passwordMismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(t('settings.passwordTooShort'));
      return;
    }
    setPasswordChanging(true);
    try {
      const res = await fetch(apiUrl('/api/auth/change-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setPasswordSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError((data as { message?: string }).message || t('settings.passwordChangeFailed'));
      }
    } catch (e) {
      setPasswordError(t('settings.requestError'));
    } finally {
      setPasswordChanging(false);
    }
  };

  const appendAuditListFilters = (params: URLSearchParams) => {
    if (auditFilterUser) params.set('user_email', auditFilterUser);
    if (auditFilterAction) params.set('action', auditFilterAction);
    if (auditFilterEntityType) params.set('entity_type', auditFilterEntityType);
    if (auditFilterFrom) params.set('from', new Date(auditFilterFrom).toISOString());
    if (auditFilterTo) params.set('to', new Date(auditFilterTo).toISOString());
  };

  const loadAuditFirstPage = useCallback(async () => {
    if (user?.role !== 'Admin' || settingsTab !== 'audit-log') return;
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(AUDIT_PAGE_SIZE));
      params.set('offset', '0');
      appendAuditListFilters(params);
      const r = await fetch(apiUrl(`/api/admin/audit-log?${params}`), { credentials: 'include' });
      const data = r.ok ? await r.json() : { entries: [] };
      const batch = (data.entries || []) as AuditTableRow[];
      setAuditEntries(batch);
      setAuditHasMore(batch.length >= AUDIT_PAGE_SIZE);
    } catch {
      setAuditEntries([]);
      setAuditHasMore(false);
    } finally {
      setAuditLoading(false);
    }
  }, [user?.role, settingsTab, auditFilterUser, auditFilterAction, auditFilterEntityType, auditFilterFrom, auditFilterTo]);

  const loadAuditMore = useCallback(async () => {
    if (user?.role !== 'Admin' || settingsTab !== 'audit-log') return;
    setAuditLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(AUDIT_PAGE_SIZE));
      params.set('offset', String(auditEntriesRef.current.length));
      appendAuditListFilters(params);
      const r = await fetch(apiUrl(`/api/admin/audit-log?${params}`), { credentials: 'include' });
      const data = r.ok ? await r.json() : { entries: [] };
      const batch = (data.entries || []) as AuditTableRow[];
      setAuditEntries((prev) => [...prev, ...batch]);
      setAuditHasMore(batch.length >= AUDIT_PAGE_SIZE);
    } catch {
      setAuditHasMore(false);
    } finally {
      setAuditLoadingMore(false);
    }
  }, [user?.role, settingsTab, auditFilterUser, auditFilterAction, auditFilterEntityType, auditFilterFrom, auditFilterTo]);

  useEffect(() => {
    void loadAuditFirstPage();
  }, [loadAuditFirstPage]);

  const downloadAuditExport = async (format: 'csv' | 'json') => {
    setAuditExportLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('format', format);
      appendAuditListFilters(params);
      if (auditCategoryFilter !== 'all') params.set('action_category', auditCategoryFilter);
      const url = apiUrl(`/api/admin/audit-log/export?${params}`);
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return;
      if (format === 'json') {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data.entries ?? data, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `audit-log-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
      } else {
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `audit-log-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } finally {
      setAuditExportLoading(false);
    }
  };

  const visibleAuditEntries = useMemo(() => {
    let list = auditEntries;
    if (auditCategoryFilter !== 'all') {
      list = list.filter((e) => getRowCategory(e) === auditCategoryFilter);
    }
    if (auditOnlyWithDiff) {
      list = list.filter((e) => (getAuditPayloadChanged(e.payload)?.length ?? 0) > 0);
    }
    return list;
  }, [auditEntries, auditCategoryFilter, auditOnlyWithDiff]);

  useEffect(() => {
    if (user?.role !== 'Admin' || settingsTab !== 'error-logs') return;
    setErrorLogsLoading(true);
    fetch(apiUrl('/api/admin/error-logs?limit=100'), { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { logs: [] }))
      .then((data) => setErrorLogs(data.logs || []))
      .catch(() => setErrorLogs([]))
      .finally(() => setErrorLogsLoading(false));
  }, [user?.role, settingsTab]);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/mappings/profiles', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProfiles(data);
        if (selectedProfile) {
          const updated = data.find((p: any) => p.id === selectedProfile.id);
          setSelectedProfile(updated);
        }
      }
    } catch (e) {
      console.error('Failed to load profiles:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async (profileData: any) => {
    try {
      const res = await fetch('/api/mappings/profiles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileData),
      });
      if (res.ok) {
        await loadProfiles();
        setShowProfileModal(false);
        setEditingProfile(null);
      }
    } catch (e) {
      console.error('Failed to create profile:', e);
    }
  };

  const handleUpdateProfile = async (id: string, profileData: any) => {
    try {
      const res = await fetch(`/api/mappings/profiles/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileData),
      });
      if (res.ok) {
        await loadProfiles();
        setShowProfileModal(false);
        setEditingProfile(null);
      }
    } catch (e) {
      console.error('Failed to update profile:', e);
    }
  };

  const handleDeleteProfile = async (id: string) => {
    if (!confirm(t('settings.deleteProfileConfirm'))) return;
    try {
      const res = await fetch(`/api/mappings/profiles/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        await loadProfiles();
        if (selectedProfile?.id === id) {
          setSelectedProfile(null);
        }
      }
    } catch (e) {
      console.error('Failed to delete profile:', e);
    }
  };

  const save = async () => {
    if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
      localStorage.setItem('google_client_id', clientId);
    }

    if (user?.role === 'Admin') {
      // Use FormData if stamp image is provided, otherwise use JSON
      let requestBody: FormData | string;
      let headers: HeadersInit = { 'Content-Type': 'application/json' };

      if (aiLeadClassEnabled) {
        const n = aiLeadCategories.length;
        if (n < 3 || n > 5) {
          toast({
            title: t('common.error'),
            description: t('settings.aiLeadClassCategoryCount', 'Az AI Lead besoroláshoz 3–5 kategória szükséges.'),
            variant: 'destructive',
          });
          return;
        }
        if (!aiEmptyNoteCanonical?.trim()) {
          toast({
            title: t('common.error'),
            description: t('settings.aiLeadClassEmptyStage', 'Válassz szakaszt az „Üres megjegyzés” sorhoz.'),
            variant: 'destructive',
          });
          return;
        }
        if (
          aiLeadCategories.some(
            (c) => !c.label.trim() || !c.description.trim() || !c.stageCanonical.trim()
          )
        ) {
          toast({
            title: t('common.error'),
            description: t(
              'settings.aiLeadClassRowIncomplete',
              'Minden kategória sorban töltsd ki a nevet, leírást és a közös szakaszt.'
            ),
            variant: 'destructive',
          });
          return;
        }
        if (subpipelines.length > 1 && aiLeadCommonStageOptions.length === 0) {
          toast({
            title: t('common.error'),
            description: t(
              'settings.aiLeadClassNoCommonStages',
              'Nincs olyan szakasz, ami minden alpipeline-ban megjelenik. Egységesítsd a szakaszneveket, vagy vedezz kevesebb alpipeline-t.'
            ),
            variant: 'destructive',
          });
          return;
        }
      }

      const cleanInvoiceMap = Object.fromEntries(
        Object.entries(invoicePaidBySubpipeline).filter(([, v]) => v && String(v).trim())
      );
      const cleanMeasureMap = Object.fromEntries(
        Object.entries(measureBySubpipeline).filter(([, v]) => v && String(v).trim())
      );
      const cleanClosingDateOffsetMap = Object.fromEntries(
        Object.entries(closingDateOffsetByStageId).flatMap(([stageId, days]) => {
          const sid = String(stageId || '').trim();
          if (!sid) return [];
          const n = Number(days);
          if (!Number.isFinite(n)) return [];
          const clamped = Math.max(0, Math.min(7, Math.trunc(n)));
          return [[sid, clamped]];
        })
      ) as Record<string, number>;
      const automationsPayload = {
        onTaxDocumentZmluvaEmail: { enabled: automationOnTaxDocumentZmluvaEmail },
        onInvoicePaid: { stagesBySubpipelineId: cleanInvoiceMap },
        onMeasureComplete: { stagesBySubpipelineId: cleanMeasureMap },
        closingDateOnStage: {
          enabled: closingDateOnStageEnabled,
          offsetDaysByStageId: cleanClosingDateOffsetMap,
          cooldownMinutes: 5,
        },
        aiLeadClassification: aiLeadClassEnabled
          ? {
              enabled: true,
              categories: aiLeadCategories.map((c) => ({
                key: c.key.trim() || crypto.randomUUID(),
                label: c.label.trim(),
                description: c.description.trim(),
                stageCanonical: c.stageCanonical.trim(),
                sendQuoteEmail: c.sendQuoteEmail !== false,
              })),
              emptyNote: { stageCanonical: aiEmptyNoteCanonical.trim(), sendQuoteEmail: aiEmptyNoteSendQuoteEmail },
            }
          : {
              enabled: false,
              categories: aiLeadCategories.map((c) => ({
                key: c.key.trim() || crypto.randomUUID(),
                label: c.label.trim(),
                description: c.description.trim(),
                stageCanonical: c.stageCanonical.trim(),
                sendQuoteEmail: c.sendQuoteEmail !== false,
              })),
              emptyNote: { stageCanonical: aiEmptyNoteCanonical.trim() || '', sendQuoteEmail: aiEmptyNoteSendQuoteEmail },
            },
      };
      if (supplierStampImage) {
        const formData = new FormData();
        formData.append('manualsFolderId', folderId);
        formData.append('measureTargetStageId', '');
        formData.append('supplierStampImage', supplierStampImage);
        formData.append('automations', JSON.stringify(automationsPayload));
        requestBody = formData;
        headers = {}; // Let browser set Content-Type with boundary for multipart
      } else {
        const payload = {
          manualsFolderId: folderId,
          measureTargetStageId: null,
          supplierStampImageUrl: supplierStampImageUrl || null,
          automations: automationsPayload,
        };
        requestBody = JSON.stringify(payload);
      }

      try {
        const response = await fetch('/api/admin/settings', {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: requestBody,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[SETTINGS] save failed: ${response.status}`, errorText);
          toast({
            title: t('common.error'),
            description: t('settings.saveError', { status: String(response.status) }),
            variant: 'destructive',
          });
          return;
        }

        await response.json().catch(() => null);
        companyBaselineRef.current = getCompanyFingerprint();
        setCompanyDirty(false);
      } catch (error) {
        console.error('[SETTINGS] save exception:', error);
        toast({
          title: t('common.error'),
          description: t('settings.saveError', {
            status: error instanceof Error ? error.message : String(error),
          }),
          variant: 'destructive',
        });
        return;
      }
    }

    setSaved(true);
  };

  const currentNavTitle = useMemo(() => {
    if (settingsTab === 'route-generator') {
      return t('settings.routeGenerator');
    }
    if (settingsTab === 'variables') {
      const sub = VARIABLES_SUB_ITEMS.find((s) => s.value === variablesSubTab);
      return sub ? `${t('settings.catalogAndContent')} · ${t(sub.labelKey)}` : t('settings.catalogAndContent');
    }
    if (settingsTab === 'email-and-docs') {
      const sub = EMAIL_DOCS_SUB_ITEMS.find((s) => s.value === emailDocsSubTab);
      return sub ? `${t('settings.emailAndDocs')} · ${t(sub.labelKey)}` : t('settings.emailAndDocs');
    }
    const flat = settingsMenuGroups.flatMap((gr) => gr.items);
    const g = flat.find((i) => i.value === settingsTab);
    return g ? t(g.labelKey) : t('settings.title');
  }, [settingsTab, variablesSubTab, emailDocsSubTab, settingsMenuGroups, t]);

  const scrollToMappingsSection = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const savePreferences = async () => {
    if (!user?.email) return;
    setPreferencesSaving(true);
    setPreferencesSaved(false);
    try {
      const preferences: Record<string, unknown> = {
        crmSortBy: prefCrmSortBy,
        crmSortOrder: prefCrmSortOrder,
      };
      if (prefCrmStartExpanded) {
        preferences.crmCollapsedColumns = {};
      }
      const res = await fetch(apiUrl('/api/user/preferences'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferences }),
      });
      if (res.ok) {
        setPreferencesSaved(true);
        setTimeout(() => setPreferencesSaved(false), 3000);
      }
    } catch (e) {
      console.error('Failed to save preferences:', e);
    } finally {
      setPreferencesSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="relative z-20 flex h-[60px] min-h-[60px] flex-shrink-0 overflow-hidden border-b bg-background">
        <PageTitleBar
          className="h-full border-0"
          rowClassName="h-full min-h-0 items-center"
          title={t('settings.title')}
          right={
            isMobile ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-9 shrink-0 p-0"
                onClick={() => setMobileSettingsNavOpen(true)}
                aria-label={t("settings.sections")}
              >
                <Menu className="h-4 w-4 shrink-0" aria-hidden />
              </Button>
            ) : null
          }
        />
      </div>
      
      {/* Content: desktop bal oldali nav kártya; mobil Sheet menü */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <div className={cn("flex-1 min-h-0", isMobile ? "overflow-y-auto" : "overflow-hidden")}>
          <div className={cn(isMobile ? "px-4 py-6 max-w-screen-lg mx-auto" : "h-full grid grid-cols-[360px,1fr] gap-3 md:gap-6")}>
            <div className="hidden md:block overflow-y-auto shrink-0">
              <div className="p-4">
                <div className="p-4 border rounded-md bg-card sticky top-0">
                  <div className="text-sm font-medium mb-2 text-foreground">{t('settings.sections')}</div>
                  <SettingsNavPanel
                    variant="sidebar"
                    groups={settingsMenuGroups}
                    navFilter={settingsNavFilter}
                    onNavFilterChange={setSettingsNavFilter}
                    navFilterLower={navFilterLower}
                    labelMatchesFilter={labelMatchesFilter}
                    settingsTab={settingsTab}
                    variablesSubTab={variablesSubTab}
                    emailDocsSubTab={emailDocsSubTab}
                    onSelectTab={trySelectSettingsTab}
                    onSelectVariablesSub={setVariablesSubTab}
                    onSelectEmailDocsSub={setEmailDocsSubTab}
                  />
                </div>
              </div>
            </div>

            {/* Jobb oldal: tartalom; asztalon csak ez görget */}
            <div className={cn("min-w-0", !isMobile && "flex-1 overflow-y-auto")}>
              <div className={cn(
                "py-3 md:py-6 space-y-4 md:space-y-6",
                isMobile ? "px-4 max-w-screen-lg mx-auto" : "container max-w-5xl mr-auto pl-2 md:pl-4 pr-4 md:pr-6 pb-10 md:pb-12"
              )}>
      <Tabs value={settingsTab} onValueChange={(value) => trySelectSettingsTab(value as SettingsTab)} className="w-full min-w-0">
        <TabsList
          className="sr-only"
          aria-label={t('settings.sections')}
        >
          {(user?.role === 'Admin' ? ALL_SETTINGS_TAB_IDS : (['profile', 'preferences'] as const)).map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {t(SETTINGS_TAB_SR_KEYS[tab])}
            </TabsTrigger>
          ))}
        </TabsList>
        {isMobile && <p className="text-sm text-muted-foreground mb-1">{currentNavTitle}</p>}

        {/* Profil – mindenki; megjelenített név, jelszóváltás (email auth esetén) */}
        <TabsContent value="profile" className="mt-4">
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-1">{t('settings.profileTitle')}</h2>
              <p className="text-sm text-muted-foreground">{t('settings.profileSubtitle')}</p>
            </div>
            {user ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    {(profilePicturePreview || user.picture) ? (
                      <img
                        src={profilePicturePreview || user.picture || ''}
                        alt={t('settings.profileTitle')}
                        className="h-16 w-16 rounded-full object-cover border-2 border-border"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center border-2 border-border text-lg font-semibold text-muted-foreground">
                        {(user.name || user.email || 'U').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                    <p className="text-xs mt-1">{t('settings.roleLabel')}: <span className="font-semibold">{user.role || t('settings.roleUser')}</span></p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.profilePicture')}</Label>
                  <p className="text-xs text-muted-foreground">{t('settings.profilePictureHint')}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={profileFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="sr-only"
                      aria-hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setProfilePictureFile(file);
                          const url = URL.createObjectURL(file);
                          setProfilePicturePreview(url);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => profileFileInputRef.current?.click()}
                    >
                      {t('settings.chooseFile')}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {profilePictureFile ? profilePictureFile.name : t('settings.noFileSelected')}
                    </span>
                    {profilePictureFile && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setProfilePictureFile(null);
                          if (profilePicturePreview) URL.revokeObjectURL(profilePicturePreview);
                          setProfilePicturePreview(null);
                        }}
                      >
                        {t('settings.profilePictureClear')}
                      </Button>
                    )}
                    {user.picture && !profilePictureFile && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={profileSaving}
                        onClick={async () => {
                          setProfileSaving(true);
                          try {
                            const res = await fetch(apiUrl('/api/user/profile'), {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({ name: profileName.trim() || user?.email, picture: null }),
                            });
                            if (res.ok) {
                              await refreshUser();
                            }
                          } finally {
                            setProfileSaving(false);
                          }
                        }}
                      >
                        {t('settings.profilePictureRemove')}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.displayedName')}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      placeholder={user.email}
                      className="max-w-xs"
                    />
                    <Button size="sm" onClick={saveProfile} disabled={profileSaving}>
                      {profileSaving ? t('settings.saving') : t('settings.saveButton')}
                    </Button>
                    {profileSaved && <span className="text-sm text-muted-foreground">{t('settings.saved')}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">{t('settings.profileSaveHint')}</p>
                </div>
                {authMode === 'email' && (
                  <div className="space-y-3 pt-4 border-t">
                    <h3 className="text-lg font-medium">{t('settings.changePassword')}</h3>
                    <p className="text-sm text-muted-foreground">{t('settings.changePasswordHint')}</p>
                    <div className="space-y-2 max-w-xs">
                      <Label>{t('settings.currentPassword')}</Label>
                      <Input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="••••••••"
                        autoComplete="current-password"
                      />
                      <Label>{t('settings.newPassword')}</Label>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                      />
                      <Label>{t('settings.confirmNewPassword')}</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                      />
                      {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
                      {passwordSuccess && <p className="text-sm text-green-600 dark:text-green-400">{t('settings.passwordChanged')}</p>}
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" onClick={handleChangePassword} disabled={passwordChanging}>
                          {passwordChanging ? t('settings.saving') : t('settings.savePassword')}
                        </Button>
                        <Link to="/forgot-password" className="text-sm text-primary underline">
                          {t('settings.forgotPassword')}
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
                {authMode === 'email' && (
                  <div className="space-y-2 pt-4 border-t">
                    <h3 className="text-lg font-medium">{t('settings.activeSessions')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.activeSessionsHint')}
                    </p>
                    <Button variant="outline" onClick={handleLogoutOthers} disabled={logoutOthersLoading}>
                      {logoutOthersLoading ? t('settings.processing') : t('settings.logoutElsewhere')}
                    </Button>
                    {logoutOthersDone && <p className="text-sm text-green-600 dark:text-green-400">{t('settings.logoutElsewhereSuccess')}</p>}
                  </div>
                )}
                <div className="space-y-2 pt-4 border-t">
                  <h3 className="text-lg font-medium">{t('settings.exportData')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.exportDataHint')}
                  </p>
                  <Button variant="outline" onClick={handleExportData} disabled={exportLoading}>
                    {exportLoading ? t('settings.downloading') : t('settings.downloadMyData')}
                  </Button>
                </div>
                <div className="space-y-2 pt-4 border-t">
                  <h3 className="text-lg font-medium text-destructive">{t('settings.deleteAccount')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.deleteAccountHint')}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      placeholder={t('settings.emailConfirmPlaceholder')}
                      value={deleteAccountConfirm}
                      onChange={(e) => setDeleteAccountConfirm(e.target.value)}
                      className="max-w-xs border-destructive/50"
                    />
                    <Button
                      variant="destructive"
                      onClick={async () => {
                        if (deleteAccountConfirm.trim().toLowerCase() !== user?.email?.toLowerCase()) {
                          setDeleteAccountError(t('settings.emailMismatch'));
                          return;
                        }
                        if (!confirm(t('confirm.deleteAccount'))) return;
                        setDeleteAccountLoading(true);
                        setDeleteAccountError('');
                        try {
                          const res = await fetch(apiUrl('/api/user/delete-account'), { method: 'POST', credentials: 'include' });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok) {
                            window.location.href = '/login';
                          } else {
                            setDeleteAccountError((data as { message?: string }).message || t('settings.failed'));
                          }
                        } catch (e) {
                          setDeleteAccountError(t('settings.requestError'));
                        } finally {
                          setDeleteAccountLoading(false);
                        }
                      }}
                      disabled={deleteAccountLoading || deleteAccountConfirm.trim().toLowerCase() !== user?.email?.toLowerCase()}
                    >
                      {deleteAccountLoading ? t('settings.deleting') : t('settings.deleteAccount')}
                    </Button>
                  </div>
                  {deleteAccountError && <p className="text-sm text-destructive">{deleteAccountError}</p>}
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">{t('settings.notLoggedIn')}</p>
            )}
          </div>
        </TabsContent>

        {/* Személyes beállítások – mindenki */}
        <TabsContent value="preferences" className="mt-4">
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-1">{t('settings.preferencesTitle')}</h2>
              <p className="text-sm text-muted-foreground">{t('settings.preferencesSubtitle')}</p>
            </div>

            <div className="space-y-3">
              <Label>{t('settings.language')}</Label>
              <LanguageSwitcher variant="select" className="w-[200px]" />
            </div>

            <div className="space-y-3">
              <Label>{t('settings.appearanceTheme')}</Label>
              <RadioGroup
                value={theme}
                onValueChange={(v) => setTheme(v as 'light' | 'dark' | 'system')}
                className="flex flex-col gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="light" id="theme-light" />
                  <Label htmlFor="theme-light" className="font-normal cursor-pointer">{t('settings.themeLight')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dark" id="theme-dark" />
                  <Label htmlFor="theme-dark" className="font-normal cursor-pointer">{t('settings.themeDark')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="system" id="theme-system" />
                  <Label htmlFor="theme-system" className="font-normal cursor-pointer">{t('settings.themeSystem')}</Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground">{t('settings.themeHint')}</p>
            </div>

            <div className="space-y-3">
              <Label>{t('settings.designVariant')}</Label>
              <RadioGroup
                value={designVariant}
                onValueChange={(v) => setDesignVariant(v as 'default' | 'figma')}
                className="flex flex-col gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="default" id="design-default" />
                  <Label htmlFor="design-default" className="font-normal cursor-pointer">{t('settings.designVariantDefault')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="figma" id="design-figma" />
                  <Label htmlFor="design-figma" className="font-normal cursor-pointer">{t('settings.designVariantFigma')}</Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground">{t('settings.designVariantHint')}</p>
            </div>

            <div className="space-y-3">
              <Label>{t('settings.crmDefaultSort')}</Label>
              {preferencesLoaded && (
                <div className="flex flex-wrap items-center gap-3">
                  <Select value={prefCrmSortBy} onValueChange={setPrefCrmSortBy}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="createdAt">{t('crm.sortByCreatedAt')}</SelectItem>
                      <SelectItem value="title">{t('crm.sortByTitle')}</SelectItem>
                      <SelectItem value="closingDate">{t('crm.sortByClosingDate')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={prefCrmSortOrder} onValueChange={(v) => setPrefCrmSortOrder(v as 'asc' | 'desc')}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">{t('crm.sortOrderDesc')}</SelectItem>
                      <SelectItem value="asc">{t('crm.sortOrderAsc')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <p className="text-xs text-muted-foreground">{t('settings.crmDefaultSortHint')}</p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="crm-start-expanded"
                checked={prefCrmStartExpanded}
                onCheckedChange={(checked) => setPrefCrmStartExpanded(checked === true)}
              />
              <Label htmlFor="crm-start-expanded" className="font-normal cursor-pointer text-sm">
                {t('settings.crmStartExpanded')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">{t('settings.crmStartExpandedHint')}</p>

            <div className="flex gap-2">
              <Button size="sm" onClick={savePreferences} disabled={preferencesSaving}>
                {preferencesSaving ? t('settings.saving') : t('settings.saveButton')}
              </Button>
              {preferencesSaved && <span className="text-sm text-muted-foreground self-center">{t('settings.saved')}</span>}
            </div>
          </div>
        </TabsContent>

        {/* Céges beállítások – Admin */}
        <TabsContent value="company" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedCompany')} />
          ) : (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.companyTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('settings.companySubtitle')}</p>
              </div>
              <Accordion
                type="multiple"
                defaultValue={['company-files', 'company-automations', 'company-ai']}
                className="border rounded-lg px-1"
              >
                <AccordionItem value="company-files" className="border-b px-2">
                  <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                    {t('settings.companyAccordionFiles')}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-6 pb-2 pt-0">
                <div className="space-y-2">
                  <Label>{t('settings.manualsFolderId')}</Label>
                  <Input
                    value={folderId}
                    onChange={(e) => setFolderId(e.target.value)}
                    placeholder={t('settings.apiKeyPlaceholder')}
                  />
                  <p className="text-xs text-muted-foreground">{t('settings.manualsFolderHint')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.stampImage')}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={stampFileInputRef}
                      type="file"
                      accept="image/png"
                      className="sr-only"
                      aria-hidden
                      onChange={(e) => setSupplierStampImage(e.target.files?.[0] || null)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => stampFileInputRef.current?.click()}
                    >
                      {t('settings.chooseFile')}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {supplierStampImage ? supplierStampImage.name : t('settings.noFileSelected')}
                    </span>
                  </div>
                  {supplierStampImage && (
                    <p className="text-xs text-muted-foreground">{t('settings.stampSelected', { name: supplierStampImage.name })}</p>
                  )}
                  {supplierStampImageUrl && !supplierStampImage && (
                    <p className="text-xs text-muted-foreground">{t('settings.stampCurrent')} <a href={supplierStampImageUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline">{t('settings.stampView')}</a></p>
                  )}
                  <p className="text-xs text-muted-foreground">{t('settings.stampHint')}</p>
                </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="company-automations" className="border-b px-2">
                  <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                    {t('settings.companyAccordionAutomations')}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-2 pt-0">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="automation-tax-zmluva"
                      checked={automationOnTaxDocumentZmluvaEmail}
                      onCheckedChange={(checked) => setAutomationOnTaxDocumentZmluvaEmail(!!checked)}
                    />
                    <label
                      htmlFor="automation-tax-zmluva"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {t('settings.automationOnTaxDocumentZmluvaEmail', 'Daňový doklad után: Zmluva o dielo generálása + e-mail (daňový doklad + Zmluva melléklet)')}
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('settings.automationOnTaxDocumentHint', 'Ha be van kapcsolva, a dokumentumok szinkronizálása (Refresh) után, ha a dealhez új daňový doklad kerül, automatikusan generálódik a Zmluva o dielo és egy e-mail kimegy az ügyfélnek a daňový dokladdal és a Zmluvával mellékletként.')}
                  </p>
                  <div className="space-y-3 pt-3">
                    <p className="text-xs font-medium text-muted-foreground">
                      {t('settings.automationClosingDateOnStageTitle', 'Stage váltás → Closing date eltolás')}
                    </p>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="automation-closing-date-on-stage"
                        checked={closingDateOnStageEnabled}
                        onCheckedChange={(checked) => setClosingDateOnStageEnabled(!!checked)}
                      />
                      <label
                        htmlFor="automation-closing-date-on-stage"
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {t('settings.automationClosingDateOnStageEnabled', 'Bekapcsolva')}
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'settings.automationClosingDateOnStageHint',
                        'Ha egy stage-be mozgatod a deal-t, a closing date az aktuális (vagy üres esetén a mai) dátumhoz képest eltolódik. Kézi módosítás után 5 percig nem írja felül.'
                      )}
                    </p>
                    <div className={cn("space-y-2", !closingDateOnStageEnabled && "opacity-60 pointer-events-none")}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs text-muted-foreground">
                          {t("settings.automationClosingDateOnStageSubpipeline", "Subpipeline")}
                        </div>
                        <Select
                          value={closingDateOnStagePipelineId}
                          onValueChange={(v) => setClosingDateOnStagePipelineId(v)}
                          disabled={loadingPipelines}
                        >
                          <SelectTrigger className="h-9 w-[260px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {closingDatePipelineOptions.map((opt) => (
                              <SelectItem key={opt.id} value={opt.id}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 max-h-[min(360px,50vh)] overflow-y-auto pr-1">
                        {closingDateStages.map((s: any) => {
                          const offset = closingDateOffsetByStageId[s.id] ?? 0;
                          return (
                            <div
                              key={s.id}
                              className="flex items-center justify-between gap-3 rounded-md border border-border/60 p-2"
                            >
                              <div className="min-w-0">
                                <div className="text-xs font-medium truncate">{stageOptionLabel(s)}</div>
                              </div>
                              <Select
                                value={String(offset)}
                                onValueChange={(v) => {
                                  const n = Math.max(0, Math.min(7, Number(v)));
                                  setClosingDateOffsetByStageId((prev) => ({ ...prev, [s.id]: n }));
                                }}
                                disabled={loadingStages}
                              >
                                <SelectTrigger className="h-9 w-[110px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.from({ length: 8 }).map((_, i) => (
                                    <SelectItem key={i} value={String(i)}>
                                      {t(
                                        'settings.automationClosingDateOffsetOption',
                                        { days: i, defaultValue: `+${i} nap` }
                                      )}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setClosingDateOffsetByStageId({})}
                        >
                          {t('settings.automationClosingDateOnStageClear', 'Összes lenullázása')}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 pt-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {t('settings.automationOnInvoicePaidPerSubpipeline', 'Számla befizetése után — minden alpipeline-hoz külön cél szakasz (a deal subpipeline-ja nem változik).')}
                    </p>
                    <div className="space-y-3 max-h-[min(360px,50vh)] overflow-y-auto pr-1">
                      {subpipelines.map((p: any) => (
                        <div key={p.id} className="space-y-1 rounded-md border border-border/60 p-2">
                          <div className="text-xs font-medium">{pipelineLabel(p)}</div>
                          <Select
                            value={invoicePaidBySubpipeline[p.id] || '__none__'}
                            onValueChange={(v) =>
                              setInvoicePaidBySubpipeline((prev) => {
                                const next = { ...prev };
                                if (v === '__none__') delete next[p.id];
                                else next[p.id] = v;
                                return next;
                              })
                            }
                            disabled={loadingStages}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={t('settings.noAutoMovePlaceholder')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">{t('settings.noAutoMoveOption')}</SelectItem>
                              {stages
                                .filter((s: any) => stagePipelineId(s) === p.id)
                                .map((s: any) => (
                                  <SelectItem key={s.id} value={s.id}>
                                    {stageLabel(s)}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'settings.automationOnInvoicePaidHint',
                        'Webhook: a deal aktuális subpipeline-jához tartozó sor szerinti sablon szakaszt név alapján párosítjuk ugyanabban a subpipeline-ban.'
                      )}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t('settings.measureTargetPerSubpipeline', 'Bemérés befejezése után — minden alpipeline-hoz külön cél szakasz (subpipeline változatlan).')}
                  </p>
                  <div className="space-y-3 max-h-[min(360px,50vh)] overflow-y-auto pr-1">
                    {subpipelines.map((p: any) => (
                      <div key={p.id} className="space-y-1 rounded-md border border-border/60 p-2">
                        <div className="text-xs font-medium">{pipelineLabel(p)}</div>
                        <Select
                          value={measureBySubpipeline[p.id] || '__none__'}
                          onValueChange={(v) =>
                            setMeasureBySubpipeline((prev) => {
                              const next = { ...prev };
                              if (v === '__none__') delete next[p.id];
                              else next[p.id] = v;
                              return next;
                            })
                          }
                          disabled={loadingStages}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('settings.noAutoMovePlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">{t('settings.noAutoMoveOption')}</SelectItem>
                            {stages
                              .filter((s: any) => stagePipelineId(s) === p.id)
                              .map((s: any) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {stageLabel(s)}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'settings.measureTargetHintKeepSubpipeline',
                      'A deal a saját subpipeline-jában marad; a kiválasztott szakasz nevét ott párosítjuk.'
                    )}
                  </p>
                </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="company-ai" className="border-b px-2">
                  <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                    {t('settings.companyAccordionAi')}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-2 pt-0">
                <div className="space-y-4">
                  <p className="text-xs text-muted-foreground max-w-3xl">
                    {t(
                      'settings.aiLeadClassHint',
                      'A megjegyzés alapján háttérben kategorizálunk. A deal alpipeline-ja (Facebook űrlap / konfigurátor) nem változik — csak a szakasz azon belül. Csak olyan szakaszok választhatók, amelyek minden alpipeline-ban ugyanazzal a névvel (HU/SK/EN) szerepelnek. A belső AI-kulcsot a rendszer generálja.'
                    )}
                  </p>
                  {subpipelines.length > 1 && aiLeadCommonStageOptions.length === 0 && (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      {t(
                        'settings.aiLeadClassNoCommonStagesHint',
                        'Jelenleg nincs közös szakasz az összes alpipeline-ban — ellenőrizd a szakasznevek egyezését.'
                      )}
                    </p>
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="ai-lead-class-enabled"
                      checked={aiLeadClassEnabled}
                      onCheckedChange={(checked) => setAiLeadClassEnabled(!!checked)}
                    />
                    <label
                      htmlFor="ai-lead-class-enabled"
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      {t('settings.aiLeadClassEnabled', 'AI besorolás bekapcsolva')}
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={aiLeadCategories.length >= 5}
                      onClick={() =>
                        setAiLeadCategories((prev) => [
                          ...prev,
                          { key: '', label: '', description: '', stageCanonical: '', sendQuoteEmail: true },
                        ])
                      }
                    >
                      {t('settings.aiLeadAddCategory', 'Kategória hozzáadása')}
                    </Button>
                    <span className="text-xs text-muted-foreground">{aiLeadCategories.length} / 5</span>
                  </div>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs min-w-[120px]">{t('settings.aiLeadColLabel', 'Név')}</TableHead>
                          <TableHead className="text-xs min-w-[220px]">
                            {t('settings.aiLeadColDesc', 'Leírás (AI)')}
                          </TableHead>
                          <TableHead className="text-xs min-w-[200px]">
                            {t('settings.aiLeadColStageCommon', 'Szakasz (közös minden alpipeline-ban)')}
                          </TableHead>
                          <TableHead className="text-xs w-20 text-center">
                            {t('settings.aiLeadColSendEmail', 'Email')}
                          </TableHead>
                          <TableHead className="text-xs w-16">{t('settings.aiLeadColActions', '')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {aiLeadCategories.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="p-2 align-top">
                              <Input
                                className="h-8 text-xs"
                                value={row.label}
                                onChange={(e) =>
                                  setAiLeadCategories((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], label: e.target.value };
                                    return next;
                                  })
                                }
                              />
                            </TableCell>
                            <TableCell className="p-2 align-top">
                              <Textarea
                                className="min-h-[56px] text-xs resize-y"
                                value={row.description}
                                onChange={(e) =>
                                  setAiLeadCategories((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], description: e.target.value };
                                    return next;
                                  })
                                }
                              />
                            </TableCell>
                            <TableCell className="p-2 align-top">
                              <Select
                                value={row.stageCanonical || '__none__'}
                                onValueChange={(v) =>
                                  setAiLeadCategories((prev) => {
                                    const next = [...prev];
                                    next[idx] = {
                                      ...next[idx],
                                      stageCanonical: v === '__none__' ? '' : v,
                                      legacyStageId: undefined,
                                    };
                                    return next;
                                  })
                                }
                                disabled={loadingStages || aiLeadCommonStageOptions.length === 0}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder={t('settings.noAutoMovePlaceholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">{t('settings.noAutoMoveOption')}</SelectItem>
                                  {aiLeadCommonStageOptions.map((opt) => (
                                    <SelectItem key={opt.fingerprint} value={opt.fingerprint}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="p-2 align-middle text-center">
                              <Checkbox
                                checked={row.sendQuoteEmail !== false}
                                onCheckedChange={(checked) =>
                                  setAiLeadCategories((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], sendQuoteEmail: !!checked };
                                    return next;
                                  })
                                }
                              />
                            </TableCell>
                            <TableCell className="p-2 align-top">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                disabled={aiLeadCategories.length <= 3}
                                onClick={() =>
                                  setAiLeadCategories((prev) => prev.filter((_, i) => i !== idx))
                                }
                              >
                                −
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell className="p-2 text-xs font-medium align-middle">
                            {t('settings.aiLeadEmptyNoteRow', 'Üres megjegyzés')} ({t('settings.aiLeadEmptyNoteKey', 'empty_note')})
                          </TableCell>
                          <TableCell className="p-2 text-xs text-muted-foreground align-middle">
                            {t('settings.aiLeadEmptyNoteDesc', 'Ha nincs szöveg a megjegyzésben.')}
                          </TableCell>
                          <TableCell className="p-2 align-top">
                            <Select
                              value={aiEmptyNoteCanonical || '__none__'}
                              onValueChange={(v) => {
                                setAiEmptyNoteCanonical(v === '__none__' ? '' : v);
                                setAiEmptyNoteLegacyStageId('');
                              }}
                              disabled={loadingStages || aiLeadCommonStageOptions.length === 0}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder={t('settings.noAutoMovePlaceholder')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">{t('settings.noAutoMoveOption')}</SelectItem>
                                {aiLeadCommonStageOptions.map((opt) => (
                                  <SelectItem key={opt.fingerprint} value={opt.fingerprint}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="p-2 align-middle text-center">
                            <Checkbox
                              checked={aiEmptyNoteSendQuoteEmail}
                              onCheckedChange={(checked) => setAiEmptyNoteSendQuoteEmail(!!checked)}
                            />
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
                  </AccordionContent>
                </AccordionItem>
                {!import.meta.env.VITE_GOOGLE_CLIENT_ID && (
                  <AccordionItem value="company-dev" className="border-b-0 px-2">
                    <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                      {t('settings.companyAccordionDev')}
                    </AccordionTrigger>
                    <AccordionContent className="pb-2 pt-0">
                      <div className="space-y-2">
                        <Label>{t('settings.googleClientIdLabel')}</Label>
                        <Input
                          value={clientId}
                          onChange={(e) => setClientId(e.target.value)}
                          placeholder={t('settings.googleClientIdPlaceholder')}
                        />
                        <p className="text-xs text-muted-foreground">{t('settings.googleClientIdHint')}</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
              <p className="text-sm text-muted-foreground">
                {t('settings.moreRouteGeneratorLink')}{' '}
                <button
                  type="button"
                  onClick={() => trySelectSettingsTab('route-generator')}
                  className="text-primary underline font-medium"
                >
                  {t('settings.routeGenerator')}
                </button>
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={save}>{t('settings.saveButton')}</Button>
                {saved && <span className="text-sm text-muted-foreground self-center">{t('settings.saved')}</span>}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Felhasználók – Admin */}
        <TabsContent value="users" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedUsers')} />
          ) : (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.usersTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('settings.usersSubtitle')}</p>
              </div>
              <Suspense fallback={lazyPanelFallback}>
                <AdminUsers />
              </Suspense>
            </div>
          )}
        </TabsContent>

        <TabsContent value="mappings" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedMappings')} />
          ) : (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.mappingsTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('settings.mappingsSubtitle')}</p>
              </div>

              <div className="sticky top-0 z-10 -mx-1 px-1 py-2 mb-2 flex flex-wrap items-center gap-2 bg-background/95 backdrop-blur border-b border-border/80">
                <span className="text-xs font-medium text-muted-foreground shrink-0">{t('settings.mappingsNavOnThisPage')}:</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => scrollToMappingsSection('settings-mappings-profiles')}
                >
                  {t('settings.mappingsJumpProfiles')}
                </Button>
                {selectedProfile ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => scrollToMappingsSection('settings-mappings-mapper')}
                  >
                    {t('settings.mappingsJumpMapper')}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => scrollToMappingsSection('settings-mappings-test')}
                >
                  {t('settings.mappingsJumpTest')}
                </Button>
              </div>

              <section id="settings-mappings-profiles" className="space-y-4 scroll-mt-28">
                <h3 className="text-lg font-medium">{t('settings.profilesHeading')}</h3>
                <p className="text-sm text-muted-foreground max-w-2xl">
                  {t('settings.facebookLeadsProfilesHint')}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setEditingProfile(null);
                      setShowProfileModal(true);
                    }}
                  >
                    {t('settings.newProfileButton')}
                  </Button>
                </div>
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">{t('settings.mappingsLoading')}</p>
                  </div>
                ) : profiles.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">{t('settings.noProfiles')}</p>
                  </div>
                ) : (
                  <ProfileList
                    profiles={profiles}
                    selectedProfile={selectedProfile}
                    onSelectProfile={setSelectedProfile}
                    onEditProfile={(profile) => {
                      setEditingProfile(profile);
                      setShowProfileModal(true);
                    }}
                    onDeleteProfile={handleDeleteProfile}
                  />
                )}
                {showProfileModal && (
                  <ProfileModal
                    profile={editingProfile}
                    onSave={
                      editingProfile
                        ? (data) => handleUpdateProfile(editingProfile.id, data)
                        : handleCreateProfile
                    }
                    onClose={() => {
                      setShowProfileModal(false);
                      setEditingProfile(null);
                    }}
                  />
                )}
              </section>

              {selectedProfile && selectedProfile.source === 'facebook' && (
                <section id="settings-mappings-mapper" className="space-y-4 scroll-mt-28">
                  <h3 className="text-lg font-medium">{t('zapierMapper.title')}</h3>
                  <Suspense fallback={lazyPanelFallback}>
                    <ZapierStyleMapper
                      profileId={selectedProfile.id}
                      facebookFormId={selectedProfile.formId || ''}
                      profileSource={selectedProfile.source || ''}
                    />
                  </Suspense>
                </section>
              )}

              {selectedProfile && selectedProfile.source !== 'facebook' && (
                <section id="settings-mappings-mapper" className="space-y-4 scroll-mt-28">
                  <h3 className="text-lg font-medium">{t('settings.fieldMappingsHeading')}</h3>
                  <Suspense fallback={lazyPanelFallback}>
                    <FieldMappingsManager
                      profileId={selectedProfile.id}
                      facebookFormId={selectedProfile.formId || ''}
                      profileSource={selectedProfile.source || ''}
                    />
                  </Suspense>
                </section>
              )}

              <section id="settings-mappings-test" className="space-y-4 scroll-mt-28">
                <h3 className="text-lg font-medium">{t('settings.testHeading')}</h3>
                <Suspense fallback={lazyPanelFallback}>
                  <TestPanel profileId={selectedProfile?.id ?? ''} />
                </Suspense>
              </section>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sub-pipelines" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedPipelines')} />
          ) : (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.subPipelines')}</h2>
                <p className="text-sm text-muted-foreground">{t('settings.subPipelinesSubtitle')}</p>
              </div>
              <Suspense fallback={lazyPanelFallback}>
                <SubPipelinesManager />
              </Suspense>
            </div>
          )}
        </TabsContent>

        <TabsContent value="route-generator" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedRouteGenerator')} />
          ) : (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.routeGenerator')}</h2>
                <p className="text-sm text-muted-foreground">{t('settings.routeGeneratorSubtitle')}</p>
              </div>
              <Suspense fallback={lazyPanelFallback}>
                <RouteGeneratorGlobalSettingsPanel />
              </Suspense>
            </div>
          )}
        </TabsContent>

        {/* Változók: egy fül; kattintásra kinyílik a 3 aloldal, az első (Terméktípusok) mindig nyitva */}
        <TabsContent value="variables" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedProductTypes')} />
          ) : (
            <div className="space-y-6">
              <Tabs value={variablesSubTab} onValueChange={(v) => setVariablesSubTab(v as VariablesSubTab)}>
                <TabsList className={cn('grid w-full grid-cols-3 max-w-md', 'md:hidden')}>
                  {VARIABLES_SUB_ITEMS.map((sub) => (
                    <TabsTrigger key={sub.value} value={sub.value}>{t(sub.labelKey)}</TabsTrigger>
                  ))}
                </TabsList>
                <TabsList className="hidden md:grid w-full max-w-2xl grid-cols-3 gap-1 mb-2">
                  {VARIABLES_SUB_ITEMS.map((sub) => (
                    <TabsTrigger key={sub.value} value={sub.value} className="text-xs sm:text-sm">
                      {t(sub.labelKey)}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent value="product-types" className="mt-4">
                  <div>
                    <h2 className="text-xl font-semibold mb-1">{t('settings.productTypesTitle')}</h2>
                    <p className="text-sm text-muted-foreground">{t('settings.productTypesSubtitle')}</p>
                  </div>
                  <Suspense fallback={lazyPanelFallback}>
                    <ProductTypesManager />
                  </Suspense>
                </TabsContent>
                <TabsContent value="manuals-hierarchy" className="mt-4">
                  <div>
                    <h2 className="text-xl font-semibold mb-1">{t('settings.manualsHierarchyTitle')}</h2>
                    <p className="text-sm text-muted-foreground">{t('settings.manualsHierarchySubtitle')}</p>
                  </div>
                  <Suspense fallback={lazyPanelFallback}>
                    <ManualsHierarchyManager />
                  </Suspense>
                </TabsContent>
                <TabsContent value="image-lookups" className="mt-4">
                  <div>
                    <h2 className="text-xl font-semibold mb-1">{t('settings.imageLookupsTitle')}</h2>
                    <p className="text-sm text-muted-foreground">{t('settings.imageLookupsSubtitle')}</p>
                  </div>
                  <Suspense fallback={lazyPanelFallback}>
                    <ImageLookupsManager />
                  </Suspense>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </TabsContent>

        {/* Email és dokumentumok */}
        <TabsContent value="email-and-docs" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedEmailTemplates')} />
          ) : (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.emailAndDocsTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('settings.emailAndDocsSubtitle')}</p>
              </div>
              <Tabs
                value={emailDocsSubTab}
                onValueChange={(v) => setEmailDocsSubTab(v as EmailDocsSubTab)}
                className="w-full"
              >
                <TabsList className="flex flex-wrap h-auto gap-1">
                  <TabsTrigger value="email-templates" className="text-xs sm:text-sm">
                    {t('settings.tabEmailTemplates')}
                  </TabsTrigger>
                  <TabsTrigger value="email-accounts" className="text-xs sm:text-sm">
                    {t('settings.tabEmailAccounts')}
                  </TabsTrigger>
                  <TabsTrigger value="contract-templates" className="text-xs sm:text-sm">
                    {t('settings.tabContractTemplates')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="email-templates" className="mt-4">
                  <Suspense fallback={lazyPanelFallback}>
                    <EmailTemplatesManager />
                  </Suspense>
                </TabsContent>
                <TabsContent value="email-accounts" className="mt-4">
                  <Suspense fallback={lazyPanelFallback}>
                    <EmailAccountsManager />
                  </Suspense>
                </TabsContent>
                <TabsContent value="contract-templates" className="mt-4">
                  <Suspense fallback={lazyPanelFallback}>
                    <ContractTemplatesManager />
                  </Suspense>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </TabsContent>

        <TabsContent value="google-contacts" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedGoogleContacts')} />
          ) : (
            <Suspense fallback={lazyPanelFallback}>
              <GoogleContactsSyncPanel />
            </Suspense>
          )}
        </TabsContent>

        <TabsContent value="price-generator" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedPriceGenerator', 'Csak az adminisztrátorok érik el a Generátor cien felületet.')} />
          ) : (
            <Suspense fallback={lazyPanelFallback}>
              <PriceGeneratorPanel />
            </Suspense>
          )}
        </TabsContent>

        <TabsContent value="access-configurator" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedAccessConfigurator', 'Csak az adminisztrátorok érik el az Access konfigurátort.')} />
          ) : (
            <Suspense fallback={lazyPanelFallback}>
              <AccessConfiguratorPanel />
            </Suspense>
          )}
        </TabsContent>

        <TabsContent value="audit-log" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedAuditLog')} />
          ) : (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.auditLogTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('settings.auditLogSubtitle')}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Input
                  placeholder={t('settings.filterEmailPlaceholder')}
                  value={auditFilterUser}
                  onChange={(e) => setAuditFilterUser(e.target.value)}
                  className="max-w-[200px]"
                />
                <Input
                  placeholder={t('settings.filterActionPlaceholder')}
                  value={auditFilterAction}
                  onChange={(e) => setAuditFilterAction(e.target.value)}
                  className="max-w-[180px]"
                />
                <Input
                  placeholder={t('settings.auditColumnEntity')}
                  value={auditFilterEntityType}
                  onChange={(e) => setAuditFilterEntityType(e.target.value)}
                  className="max-w-[180px]"
                />
                <Input
                  type="datetime-local"
                  value={auditFilterFrom}
                  onChange={(e) => setAuditFilterFrom(e.target.value)}
                  className="max-w-[220px]"
                />
                <Input
                  type="datetime-local"
                  value={auditFilterTo}
                  onChange={(e) => setAuditFilterTo(e.target.value)}
                  className="max-w-[220px]"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={auditExportLoading}
                  onClick={() => downloadAuditExport('csv')}
                >
                  {t('settings.auditExportCsv')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={auditExportLoading}
                  onClick={() => downloadAuditExport('json')}
                >
                  {t('settings.auditExportJson')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {AUDIT_CATEGORY_CHIPS.map((chip) => (
                  <Button
                    key={chip.id}
                    type="button"
                    size="sm"
                    variant={auditCategoryFilter === chip.id ? 'default' : 'outline'}
                    onClick={() => setAuditCategoryFilter(chip.id)}
                  >
                    {t(chip.labelKey)}
                  </Button>
                ))}
                <div className="flex items-center gap-2 ml-auto max-[640px]:ml-0 max-[640px]:basis-full">
                  <Checkbox
                    id="audit-only-diff"
                    checked={auditOnlyWithDiff}
                    onCheckedChange={(v) => setAuditOnlyWithDiff(v === true)}
                  />
                  <Label htmlFor="audit-only-diff" className="text-sm font-normal cursor-pointer">
                    {t('settings.auditOnlyWithDiff')}
                  </Label>
                </div>
              </div>
              {auditLoading ? (
                <div className="text-center py-8 border rounded-lg">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  <p className="text-muted-foreground">{t('settings.auditLoading')}</p>
                </div>
              ) : visibleAuditEntries.length === 0 ? (
                <div className="text-center py-8 border rounded-lg">
                  <p className="text-muted-foreground">{t('settings.auditNoEntries')}</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('settings.auditColumnTime')}</TableHead>
                        <TableHead>{t('settings.auditColumnUser')}</TableHead>
                        <TableHead>{t('settings.auditColumnEvent')}</TableHead>
                        <TableHead>{t('settings.auditColumnSummary')}</TableHead>
                        <TableHead>{t('settings.auditDetailColumn')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleAuditEntries.map((e) => (
                        <Fragment key={e.id}>
                          <TableRow>
                            <TableCell className="whitespace-nowrap text-muted-foreground">{new Date(e.created_at).toLocaleString(dateLocale)}</TableCell>
                            <TableCell>{e.user_email}</TableCell>
                            <TableCell className="max-w-[200px]">
                              <div className="font-medium text-sm leading-snug">{getAuditActionLabel(t, e.action)}</div>
                              <div className="text-[11px] text-muted-foreground mt-1 font-mono break-all">{getAuditTechnicalSecondLine(e)}</div>
                            </TableCell>
                            <TableCell className="max-w-[min(480px,42vw)] align-top">
                              {shouldShowAuditChangeList(e.action, e.payload) ? (
                                <AuditChangeSummary t={t} action={e.action} payload={e.payload} />
                              ) : (
                                <div className="text-sm leading-snug">{getAuditSummaryLocalized(t, e)}</div>
                              )}
                              {getAuditDeepLink(e) ? (
                                <Link
                                  to={getAuditDeepLink(e)!}
                                  className="text-xs text-primary underline-offset-2 hover:underline mt-2 inline-block"
                                >
                                  {t('settings.auditOpenDeal')}
                                </Link>
                              ) : null}
                            </TableCell>
                            <TableCell className="align-top">
                              <div className="flex flex-col gap-1 min-w-[7rem]">
                                <Button type="button" variant="secondary" size="sm" onClick={() => setAuditDetailEntry(e)}>
                                  {t('settings.auditOpenDetail')}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setAuditExpandedId((prev) => (prev === e.id ? null : e.id))}
                                >
                                  {auditExpandedId === e.id ? t('settings.auditPayloadHide') : t('settings.auditPayloadShow')}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          {auditExpandedId === e.id && (
                            <TableRow>
                              <TableCell colSpan={5} className="bg-muted/30">
                                <pre className="text-xs whitespace-pre-wrap break-all">
                                  {JSON.stringify(e.payload ?? {}, null, 2)}
                                </pre>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {!auditLoading && auditHasMore ? (
                <div className="flex justify-center pt-2">
                  <Button type="button" variant="outline" disabled={auditLoadingMore} onClick={() => void loadAuditMore()}>
                    {auditLoadingMore ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('settings.auditLoading')}
                      </>
                    ) : (
                      t('settings.auditLoadMore')
                    )}
                  </Button>
                </div>
              ) : null}
              <Sheet open={!!auditDetailEntry} onOpenChange={(open) => !open && setAuditDetailEntry(null)}>
                <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
                  {auditDetailEntry ? (
                    <>
                      <SheetHeader>
                        <SheetTitle className="text-base">{t('settings.auditDetailTitle')}</SheetTitle>
                        <SheetDescription className="text-xs font-mono break-all">
                          {new Date(auditDetailEntry.created_at).toLocaleString(dateLocale)} · {auditDetailEntry.user_email}{' '}
                          · {getAuditActionLabel(t, auditDetailEntry.action)}
                        </SheetDescription>
                      </SheetHeader>
                      <div className="mt-4 space-y-4">
                        {shouldShowAuditChangeList(auditDetailEntry.action, auditDetailEntry.payload) ? (
                          <AuditChangeSummary
                            t={t}
                            action={auditDetailEntry.action}
                            payload={auditDetailEntry.payload}
                            expandAll
                          />
                        ) : (
                          <p className="text-sm">{getAuditSummaryLocalized(t, auditDetailEntry)}</p>
                        )}
                        {getAuditDeepLink(auditDetailEntry) ? (
                          <Link
                            to={getAuditDeepLink(auditDetailEntry)!}
                            className="text-sm text-primary underline-offset-2 hover:underline inline-block"
                          >
                            {t('settings.auditOpenDeal')}
                          </Link>
                        ) : null}
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">{t('settings.auditPayloadRaw')}</p>
                          <pre className="text-[11px] whitespace-pre-wrap break-all border rounded-md p-3 bg-muted/30 max-h-[40vh] overflow-y-auto">
                            {JSON.stringify(auditDetailEntry.payload ?? {}, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </>
                  ) : null}
                </SheetContent>
              </Sheet>
            </div>
          )}
        </TabsContent>

        <TabsContent value="error-logs" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedErrorLogs')} />
          ) : (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.errorLogsTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('settings.errorLogsSubtitle')}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Input
                  placeholder={t('settings.errorLogsFilterOperation')}
                  value={errorLogsFilterOp}
                  onChange={(e) => setErrorLogsFilterOp(e.target.value)}
                  className="max-w-[220px]"
                />
              </div>
              {errorLogsLoading ? (
                <div className="text-center py-8 border rounded-lg">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  <p className="text-muted-foreground">{t('settings.errorLogsLoading')}</p>
                </div>
              ) : errorLogs.filter((e) => !errorLogsFilterOp || (e.operation || '').toLowerCase().includes(errorLogsFilterOp.toLowerCase())).length === 0 ? (
                <div className="text-center py-8 border rounded-lg">
                  <p className="text-muted-foreground">{t('settings.errorLogsEmpty')}</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('settings.errorLogsColumnTime')}</TableHead>
                        <TableHead>{t('settings.errorLogsColumnOperation')}</TableHead>
                        <TableHead>{t('settings.errorLogsColumnMessage')}</TableHead>
                        <TableHead>{t('settings.errorLogsColumnUser')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {errorLogs
                        .filter((e) => !errorLogsFilterOp || (e.operation || '').toLowerCase().includes(errorLogsFilterOp.toLowerCase()))
                        .map((e, idx) => (
                          <TableRow key={(e as any).id || idx}>
                            <TableCell className="whitespace-nowrap text-muted-foreground text-sm">{(e.timestamp || (e as any).created_at) ? new Date(e.timestamp || (e as any).created_at).toLocaleString(dateLocale) : '–'}</TableCell>
                            <TableCell className="font-mono text-sm">{e.operation || '–'}</TableCell>
                            <TableCell className="max-w-md">
                              <div className="text-sm font-medium">{e.error_message || e.message || '–'}</div>
                              {e.error_stack && <pre className="mt-1 text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap break-words">{e.error_stack.slice(0, 400)}</pre>}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">{e.user_email || '–'}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="loss-reasons" className="mt-4">
          {user?.role !== 'Admin' ? (
            <AccessDeniedMessage description={t('settings.accessDeniedLossReasons', 'Admin only')} />
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold mb-1">{t('settings.lossReasons', 'Loss reason analýza')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'settings.lossReasonsSubtitle',
                    'Closed Lost ügyletek automatikus besorolása a legutolsó jegyzet alapján (kategória + bizalom).'
                  )}
                </p>
              </div>
              <div className="rounded-lg border bg-card p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  {t('settings.lossReasonsOpenHint', 'Nyisd meg az elemző oldalt:')}
                  <span className="ml-2 font-mono text-foreground">/admin/loss-reasons</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/loss-reasons')}
                  className="gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('settings.lossReasonsOpen', 'Megnyitás')}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

      </Tabs>

              <footer className="mt-8 pt-6 border-t text-sm text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1">
                <span>{t('settings.versionLabel')} {(import.meta.env.VITE_APP_VERSION as string) || '1.0.0'}</span>
                <Link to="/docs" className="text-primary underline hover:no-underline">
                  {t('settings.documentationHelp')}
                </Link>
              </footer>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Sheet open={mobileSettingsNavOpen} onOpenChange={setMobileSettingsNavOpen}>
        <SheetContent side="left" className="w-[min(100vw-1rem,20rem)] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t('settings.sections')}</SheetTitle>
            <SheetDescription>{t('settings.sectionsMenuHint')}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <SettingsNavPanel
              variant="sheet"
              groups={settingsMenuGroups}
              navFilter={settingsNavFilter}
              onNavFilterChange={setSettingsNavFilter}
              navFilterLower={navFilterLower}
              labelMatchesFilter={labelMatchesFilter}
              settingsTab={settingsTab}
              variablesSubTab={variablesSubTab}
              emailDocsSubTab={emailDocsSubTab}
              onSelectTab={trySelectSettingsTab}
              onSelectVariablesSub={setVariablesSubTab}
              onSelectEmailDocsSub={setEmailDocsSubTab}
              onCloseSheet={() => setMobileSettingsNavOpen(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
